import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Loader2, RefreshCw, ChevronDown, ChevronRight, Search, TrendingDown, TrendingUp } from "lucide-react";
import { Toaster, toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";

const API = window.location.origin + "/api";

// Colonna "BDD" ha un highlight color di riferimento (giallo caldo — allineato allo screenshot).
const BDD_BG = "#fdf5df"; // celle prezzo BDD
const BDD_HEADER_BG = "#d9a441"; // testata BDD (oro/senape)
const HEADER_BG = "#64748b"; // testata altri listini — grigio medio (slate-500)

const fmtEuro = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  try { return n.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  catch { return `€ ${n.toFixed(2)}`; }
};

const fmtDelta = (ins, base) => {
  // Base ≤ 0 non permette il calcolo % → mostriamo dash.
  if (base === null || base === undefined || Number(base) <= 0) return null;
  if (ins === null || ins === undefined) return null;
  const pct = ((Number(ins) - Number(base)) / Number(base)) * 100;
  if (!Number.isFinite(pct)) return null;
  const sign = pct > 0 ? "+" : "";
  const abs = Math.abs(pct);
  const rounded = abs >= 100 ? pct.toFixed(1) : pct.toFixed(1);
  return { label: `${sign}${rounded}%`, positive: pct > 0, zero: pct === 0 };
};

const prettyCategory = (name) => {
  if (!name) return "Altro";
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export default function ConfrontoListiniPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ listini: [], categories: [], totalTreatments: 0 });
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/confronto-listini/matrix`, { withCredentials: true });
      setData(res);
      // Le categorie partono SEMPRE chiuse (richiesta utente): l'utente
      // apre solo quella che gli interessa. Search e filtro-categoria
      // espandono comunque il pannello in automatico.
      setExpanded(new Set());
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Errore caricamento";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMatrix(); }, []);

  const toggleCategory = (name) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };

  const allExpanded = data.categories.length > 0 && data.categories.every((c) => expanded.has(c.name));
  const toggleAll = () => {
    if (allExpanded) setExpanded(new Set());
    else setExpanded(new Set(data.categories.map((c) => c.name)));
  };

  // Filtra prestazioni per search (code + name) e categoria selezionata.
  // Se categoryFilter != "all", mostro solo la categoria scelta (tutte le
  // sue prestazioni) — la ricerca testuale è ulteriormente restrittiva.
  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cats = categoryFilter === "all"
      ? data.categories
      : data.categories.filter((c) => c.name === categoryFilter);
    if (!q) return cats;
    return cats
      .map((c) => ({
        ...c,
        treatments: c.treatments.filter((t) =>
          (t.code || "").toLowerCase().includes(q) ||
          (t.name || "").toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.treatments.length > 0);
  }, [data.categories, search, categoryFilter]);

  const listini = data.listini || [];
  const otherListini = listini.filter((l) => l !== "BDD");
  const totalVisible = filteredCategories.reduce((sum, c) => sum + c.treatments.length, 0);

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light pt-[calc(7px+3.5rem+13px)]" data-testid="confronto-listini-page">
      <Toaster position="top-right" theme="light" />
      <div className="w-full px-4 lg:px-[100px] pb-8">
        <PageHeader
          title="Confronto Listini"
          subtitle="Prezzi BDD (privato) confrontati con ogni listino assicurativo. Delta calcolato rispetto a BDD."
          actions={
            <button
              onClick={fetchMatrix}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 border border-white/30 text-white text-sm font-medium disabled:opacity-50"
              data-testid="refresh-matrix-btn"
              title="Ricarica matrice"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Aggiorna
            </button>
          }
        />

        <div className="bg-white rounded-3xl shadow-md -mt-8 relative z-10 overflow-hidden">
          {/* Toolbar: search + category filter + toggle-all + counters */}
          <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-200">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca prestazione (codice o nome)…"
                className="pl-9"
                data-testid="matrix-search-input"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => {
                const v = e.target.value;
                setCategoryFilter(v);
                // Quando si sceglie una categoria specifica, auto-espandiamo
                // il suo pannello così l'utente vede subito le prestazioni.
                if (v !== "all") setExpanded((prev) => new Set(prev).add(v));
              }}
              className="h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 min-w-[180px]"
              data-testid="matrix-category-filter"
              title="Filtra per categoria"
            >
              <option value="all">Tutte le categorie</option>
              {data.categories.map((c) => (
                <option key={c.name} value={c.name}>
                  {prettyCategory(c.name)} ({c.treatments.length})
                </option>
              ))}
            </select>
            <button
              onClick={toggleAll}
              className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="toggle-all-cats"
            >
              {allExpanded ? "Chiudi tutti" : "Apri tutti"}
            </button>
            <div className="ml-auto flex items-center gap-4 text-xs text-slate-500">
              <span data-testid="counter-treatments">
                <b className="text-slate-800">{totalVisible}</b> prestazioni
              </span>
              <span>
                <b className="text-slate-800">{otherListini.length}</b> assicurazioni
              </span>
              <span className="hidden sm:flex items-center gap-1">
                <span className="inline-flex items-center gap-1 text-slate-500"><TrendingUp className="w-3 h-3" />vs BDD</span>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1 text-rose-600"><TrendingDown className="w-3 h-3" />sconto</span>
              </span>
            </div>
          </div>

          {/* Body */}
          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-16 text-slate-500">Nessuna prestazione trovata.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]" data-testid="matrix-table">
                <thead className="sticky top-0 z-20">
                  <tr>
                    <th
                      className="text-left font-semibold text-white px-4 py-3 sticky left-0 z-30 min-w-[240px]"
                      style={{ backgroundColor: HEADER_BG }}
                    >
                      Prestazione
                    </th>
                    <th
                      className="text-left font-semibold text-white px-4 py-3 sticky left-[240px] z-30 min-w-[130px] border-l border-white/20"
                      style={{ backgroundColor: BDD_HEADER_BG }}
                      data-testid="col-header-bdd"
                    >
                      BDD (privato)
                    </th>
                    {otherListini.map((l) => (
                      <th
                        key={l}
                        className="text-left font-semibold text-white px-4 py-3 min-w-[130px] border-l border-white/20"
                        style={{ backgroundColor: HEADER_BG }}
                        data-testid={`col-header-${l.replace(/\s+/g, "-")}`}
                      >
                        {l}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((cat) => (
                    <CategoryBlock
                      key={cat.name}
                      cat={cat}
                      otherListini={otherListini}
                      totalCols={otherListini.length + 2}
                      isOpen={expanded.has(cat.name) || search.trim() !== ""}
                      onToggle={() => toggleCategory(cat.name)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryBlock({ cat, otherListini, totalCols, isOpen, onToggle }) {
  return (
    <>
      <tr
        className="bg-slate-100 hover:bg-slate-200/70 cursor-pointer"
        onClick={onToggle}
        data-testid={`category-row-${cat.name}`}
      >
        {/* Cella nome categoria: sticky-left così resta ancorata sotto
            la colonna "Prestazione" durante lo scroll orizzontale. */}
        <td
          className="px-4 py-2.5 font-semibold text-slate-800 text-sm sticky left-0 z-[7] bg-slate-100"
          style={{ minWidth: 240 }}
        >
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
            <span className="uppercase tracking-wide">{prettyCategory(cat.name)}</span>
            <span className="ml-2 text-xs font-normal text-slate-500">
              ({cat.treatments.length} prestazion{cat.treatments.length === 1 ? "e" : "i"})
            </span>
          </div>
        </td>
        {/* Cella BDD-column-width: sticky-left così sotto la colonna
            "BDD (privato)" resta uniforme durante lo scroll. */}
        <td
          className="sticky left-[240px] z-[7] bg-slate-100 border-l border-slate-200"
          style={{ minWidth: 130 }}
        />
        {/* Riempimento visivo per le colonne assicurazioni: NON sticky, scorre. */}
        <td colSpan={totalCols - 2} className="bg-slate-100" />
      </tr>
      {isOpen && cat.treatments.map((t) => (
        <TreatmentRow key={t.code} t={t} otherListini={otherListini} />
      ))}
    </>
  );
}

function TreatmentRow({ t, otherListini }) {
  const bddPrice = t.prices?.BDD;
  const bddFmt = fmtEuro(bddPrice);
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`row-${t.code}`}>
      {/* Colonna 1: code + name (sticky) */}
      <td
        className="px-4 py-2.5 sticky left-0 z-[5] bg-white group-hover:bg-slate-50"
        style={{ minWidth: 240 }}
      >
        <div className="flex flex-col leading-tight">
          <span className="font-mono text-[12px] font-bold text-slate-500 tracking-wide">{t.code}</span>
          <span className="text-slate-500 text-[13px]">{t.name}</span>
        </div>
      </td>

      {/* Colonna 2: BDD (sticky, bg giallo) */}
      <td
        className="px-4 py-2.5 sticky left-[240px] z-[5] border-l border-slate-100"
        style={{ backgroundColor: BDD_BG }}
      >
        <span className="font-semibold text-slate-800 tabular-nums">
          {bddFmt ?? <span className="text-slate-300">—</span>}
        </span>
      </td>

      {/* Colonne assicurazioni */}
      {otherListini.map((l) => {
        const price = t.prices?.[l];
        const priceFmt = fmtEuro(price);
        const delta = fmtDelta(price, bddPrice);
        return (
          <td
            key={l}
            className="px-4 py-2.5 border-l border-slate-100 align-top"
            data-testid={`cell-${t.code}-${l.replace(/\s+/g, "-")}`}
          >
            {priceFmt === null ? (
              <span className="text-slate-300">—</span>
            ) : (
              <div className="flex flex-col leading-tight">
                <span className="font-medium text-slate-800 tabular-nums">{priceFmt}</span>
                {delta && (
                  <span
                    className={`text-[11px] font-semibold tabular-nums ${
                      delta.zero
                        ? "text-slate-400"
                        : delta.positive
                        ? "text-emerald-600"
                        : "text-rose-600"
                    }`}
                  >
                    {delta.label}
                  </span>
                )}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}
